import { mode } from '@chakra-ui/theme-tools';
import { breakpoints } from './foundations/breakpoints';

/** Chakra toasts use `<Alert>`: success → `green`, error → `red`. Full scales avoid missing shades (e.g. solid error in dark mode uses `.200`). */
const brandGreenPalette = {
  50: '#EDFFF8',
  100: '#D6FFF0',
  200: '#B0FFE1',
  300: '#71FFCC',
  400: '#02F99E',
  500: '#00C075',
  600: '#00965F',
  700: '#07754D',
  800: '#076042',
  900: '#003623',
};

/** Full scale for `red` semantic token — Chakra toasts/alerts use `colorScheme: red` for errors (solid uses .600 light, .200 dark). */
const brandRedPalette = {
  50: '#FCE8E8',
  100: '#FAD1D1',
  200: '#F4A4A4',
  300: '#EF7676',
  400: '#EA4848',
  500: '#E31A1A',
  600: '#B71515',
  700: '#891010',
  800: '#5B0B0B',
  900: '#2E0505',
  950: '#170303',
};

export const globalStyles = {
  colors: {
    brand: brandGreenPalette,
    green: brandGreenPalette,
    brandScheme: {
      50: '#EFEBFF',
      100: '#E9E3FF',
      200: '#7551FF',
      300: '#7551FF',
      400: '#7551FF',
      500: '#422AFB',
      600: '#3311DB',
      700: '#02044A',
      800: '#190793',
      900: '#02044A',
    },
    brandTabs: {
      50: '#EFEBFF',
      100: '#E9E3FF',
      200: '#422AFB',
      300: '#422AFB',
      400: '#422AFB',
      500: '#422AFB',
      600: '#3311DB',
      700: '#02044A',
      800: '#190793',
      900: '#02044A',
    },
    horizonGreen: {
      50: '#E1FFF4',
      100: '#BDFFE7',
      200: '#7BFECE',
      300: '#39FEB6',
      400: '#01F99E',
      500: '#01B574',
      600: '#01935D',
      700: '#016B44',
      800: '#00472D',
      900: '#002417',
    },
    horizonOrange: {
      50: '#FFF7EB',
      100: '#FFF1DB',
      200: '#FFE2B8',
      300: '#FFD28F',
      400: '#FFC46B',
      500: '#FFB547',
      600: '#FF9B05',
      700: '#C27400',
      800: '#855000',
      900: '#422800',
      950: '#1F1200',
    },
    horizonRed: brandRedPalette,
    horizonBlue: {
      50: '#EBEFFF',
      100: '#D6DFFF',
      200: '#ADBFFF',
      300: '#8AA3FF',
      400: '#6183FF',
      500: '#3965FF',
      600: '#0036FA',
      700: '#0029BD',
      800: '#001B7A',
      900: '#000D3D',
      950: '#00071F',
    },
    horizonTeal: {
      50: '#EBFAF8',
      100: '#D7F4F2',
      200: '#AAE9E4',
      300: '#82DED6',
      400: '#59D4C9',
      500: '#33C3B7',
      600: '#299E94',
      700: '#1F756E',
      800: '#144D48',
      900: '#0B2826',
      950: '#051413',
    },
    horizonPurple: {
      50: '#EFEBFF',
      100: '#E9E3FF',
      200: '#422AFB',
      300: '#422AFB',
      400: '#7551FF',
      500: '#422AFB',
      600: '#3311DB',
      700: '#02044A',
      800: '#190793',
      900: '#11047A',
    },
    secondaryGray: {
      100: '#E0E5F2',
      200: '#E1E9F8',
      300: '#F4F7FE',
      400: '#E9EDF7',
      500: '#8F9BBA',
      600: '#A3AED0',
      700: '#707EAE',
      800: '#707EAE',
      900: '#1B2559',
    },
    red: brandRedPalette,
    blue: {
      50: '#EFF4FB',
      500: '#3965FF',
    },
    orange: {
      100: '#FFF6DA',
      500: '#FFB547',
    },
    navy: {
      50: '#d0dcfb',
      100: '#aac0fe',
      200: '#a3b9f8',
      300: '#728fea',
      400: '#3652ba',
      500: '#1b3bbb',
      600: '#24388a',
      700: '#1B254B',
      800: '#111c44',
      900: '#0b1437',
    },
    gray: {
      100: '#FAFCFE',
    },
    background: {
      100: '#F8FBFC',
      900: '#0b1437',
    },
  },
  styles: {
    global: (props: any) => ({
      body: {
        overflowX: 'hidden',
        bg: mode('background.100', 'background.900')(props),
        fontFamily: 'DM Sans',
        letterSpacing: '-0.5px',
      },
      input: {
        color: 'gray.700',
      },
      html: {
        fontFamily: 'DM Sans',
      },
      'iframe#webpack-dev-server-client-overlay': {
        display: 'none',
      },
      /** Bottom toast stack only (default); fixes inset so it centers over the admin main column at xl+. */
      '#chakra-toast-manager-bottom': {
        [`@media screen and (min-width: ${breakpoints.xl})`]: {
          left: 'var(--chakra-admin-sidebar-offset, 0px) !important',
          right: '0 !important',
        },
      },
    }),
  },
};
